import { AwsUtil } from './../authentication/aws.service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { BucketingStrategy } from './../bucket/bucketing-strategy';
import { BucketedBucketing } from './../bucket/bucketed-bucketing';
import { SurvivalHorizonService } from './../survival-horizon/survival-horizon.service';
import { AmChartsModule } from '@amcharts/amcharts3-angular';
import { MatSliderChange } from '@angular/material/slider';
import {
    SurvivalHorizonData,
    GetSurvivalHorizonServiceDataOutput,
    WebApiEndpoints,
    GetHistoricalRequest,
} from '@liquitics/transaction';
import { MetricTypes } from '@liquitics/transaction';

import { AmChartsService, AmChart } from '@amcharts/amcharts3-angular';
import { DashboardEventService } from '../dashboard/dashboard-event.service';
import { Component, OnInit, ElementRef, OnDestroy } from '@angular/core';
import { Stats } from 'fast-stats';
import { SnapshotService } from './../snapshot/snapshot.service';
import * as Papa from 'papaparse';
import { HistoricalData, HISTORICAL_DATA_METRIC_FIELDS, HistoricalOutput } from './historical-data';
import {
    HistoricalThroughputData,
    getDummyThroughputData,
    HistoricalSHData,
    getDummyCumulativeData,
    getDummySHData,
    HistoricalCumulativeData,
} from './historical-data-functions';
import { createSerialChart } from './../dashboard/dashboard-graphical-chart-generator';

const DATE_2D_METRICS: string[] = [MetricTypes.SURVIVAL_HORIZON, MetricTypes.NET_BREACH, MetricTypes.GROSS_BREACH, MetricTypes.LCR];
// tslint:disable-next-line:max-line-length
const COLORS = ['#0B2F47', '#0E3A58', '#11466A', '#13527C', '#165E8D', '#19699F', '#1C75B0', '#1E7EBD', '#218CD4', '#2B96DE', '#3D9FE1', '#4FA8E3', '#60B1E6'];
const NEGATIVE_COLORS = ['#5D091A', '#6F0B1F', '#820D24', '#950F29', '#A7112F', '#BA1234', '#CC1439', '#DF163E', '#E92048', '#EB3358', '#ED4567', '#EE5876', '#F06A85'];
// median, median +1std, median -1std

const STAT_MEDIAN = 'Median';
const STAT_PLUS_1_STD = '+1 StdDev';
const STAT_MINUS_1_STD = '-1 StdDev';

@Component({
    selector: 'app-histo',
    templateUrl: './histo.component.html',
    styleUrls: ['./histo.component.scss']
})
export class HistoComponent implements OnInit, OnDestroy {

    chart: any;
    selectedMetric: string = MetricTypes.SURVIVAL_HORIZON;
    MetricTypes: typeof MetricTypes = MetricTypes;
    metricCharts: any[] = [];
    historyTitle: string;

    currencyChoices: string[] = [];
    metricChoices: string[] = DATE_2D_METRICS;

    metric: string;
    currency: string;
    description: string;
    chartLevel = 0;

    monthsBack = 0;

    outgoingChart: AmChart;
    incomingChart: AmChart;

    // average: number;
    // min: number;
    // max: number;
    // stddev: number;

    subscription: Subscription;

    constructor(
        // private historicalService: HistoricalService,
        private awsUtil: AwsUtil,
        private amCharts: AmChartsService,
        private dashboardEventService: DashboardEventService,
        private snapshotService: SnapshotService,
        private survivalHorizonService: SurvivalHorizonService,
        private elementRef: ElementRef) {
    }

    ngOnInit() {
        this.getHistoricalData()
            .then((historicalData: any[]) => {
                if (this.metric === MetricTypes.THROUGHPUT && historicalData.length > 0) {
                    this.outgoingChart = this.createThroughputCharts(historicalData[0], 'outHistDiv');
                    this.incomingChart = this.createThroughputCharts(historicalData[1], 'inHistDiv');
                } else {
                    this.createChart(historicalData, 'Current');
                }
            });
        this.subscription = this.dashboardEventService.metricSelectionSubject.subscribe({
            next: selection => {
                this.metric = selection.metricType;
                this.currency = selection.currency.toUpperCase();
                this.description = selection.metricDescription;
                setTimeout(() => {
                    if (this.metric === MetricTypes.SURVIVAL_HORIZON) {
                        this.showTenor('0D', COLORS[0], 'Historical');
                        this.chartLevel = -1;
                    } else if (this.metric === MetricTypes.THROUGHPUT) {
                        this.getHistoricalData()
                            .then((historicalData: any[]) => {
                                this.outgoingChart = this.createThroughputCharts(historicalData[0], 'outHistDiv');
                                this.incomingChart = this.createThroughputCharts(historicalData[1], 'inHistDiv');
                            });
                    } else {
                        this.getHistoricalData()
                            .then((historicalData: any[]) => {
                                this.createChart(historicalData, 'Current');
                            });
                    }
                }, 250);
            }
        });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    redrawChart() {
        this.chart.invalidateSize();
    }

    resize(): void {
        const h = this.elementRef.nativeElement.parentNode.offsetHeight;
        document.getElementById('chartdiv').style.height = h + 'px';
        this.chart.invalidateSize();
    }

    monthsBackChanged(event: MatSliderChange) {
        this.monthsBack = event.value;
        this.getHistoricalData(this.monthsBack)
            .then((historicalData: any[]) => {
                this.createChart(historicalData, this.monthsBackTitle());
            });
    }

    private statisticsByFieldName(fieldName: string, historicalData: HistoricalData[]): any {
        const dataArray = historicalData.map((hd: HistoricalData) => {
            return parseFloat(hd[fieldName]);
        });
        const stats = new Stats();
        stats.push(dataArray);
        const median = Math.round(stats.median() * 100.0) / 100;
        const stddev = Math.round(stats.stddev() * 100.0) / 100;

        const fieldStats = {};
        fieldStats[STAT_MEDIAN] = median;
        fieldStats[STAT_PLUS_1_STD] = median + stddev;
        fieldStats[STAT_MINUS_1_STD] = median - stddev;

        return fieldStats;
    }

    private getHistoricalData(monthsBack?: number): Promise<any[]> {
        if (!this.currency || !this.metric) {
            return new Promise<any[]>((resolve, reject) => {
                resolve([]);
                return;
            });
        }
        const historicalRequest: GetHistoricalRequest = { metric: this.metric, currency: this.currency };
        let historicalData = [];
        this.awsUtil.webAPICall(WebApiEndpoints.QueryHistorical, historicalRequest)
            .then((payload: any) => {
                if (this.metric === MetricTypes.THROUGHPUT) {
                    // const historicalThroughputData = getDummyThroughputData();
                    const throughputHistoricalOutput = new HistoricalThroughputData(payload, 'Throughput');
                    historicalData = throughputHistoricalOutput.prepareThroughputTrends(payload, 'amount');
                    if (historicalData.length > 0) {
                        return new Promise<any[]>((resolve, reject) => {
                            resolve(historicalData);
                            return;
                        });
                    } else {
                        return new Promise<any[]>((resolve, reject) => {
                            resolve([]);
                            return;
                        });
                    }
                } else {
                    return new Promise<any[]>((resolve, reject) => {
                        if (!this.currency || !this.metric) {
                            resolve([]);
                            return;
                        }
                        /* const historicalData: HistoricalData[] = this.snapshotService.historicalData(this.currency, this.metric)
                            .sort((d1: HistoricalData, d2: HistoricalData) => {
                                return d1.date < d2.date ? 1 : -1;
                            });
                        */
                        const historicalBalanceInput = getDummyCumulativeData();
                        const historicalCumulativeData = new HistoricalCumulativeData(historicalBalanceInput, 'Cumulative');
                        historicalData = historicalCumulativeData.prepareHistoricalDataOutput(historicalBalanceInput, 'amount')[0]['data'];
                        console.log(historicalData);
                        const statisticsByFieldName = {};
                        HISTORICAL_DATA_METRIC_FIELDS.map((fieldName: string, index: number) => {
                            const fieldStats = this.statisticsByFieldName(fieldName, historicalData);
                            statisticsByFieldName[fieldName] = fieldStats;
                        });
                        if (monthsBack > 0) {
                            monthsBack = Math.min(monthsBack, historicalData.length);
                            const monthsBackData = historicalData
                                .slice(monthsBack - 1, monthsBack)[0];
                            if (!monthsBackData) {
                                resolve([]);
                                return;
                            }
                            const dataProvider = HISTORICAL_DATA_METRIC_FIELDS.map((fieldName: string, index: number) => {
                                const color = parseFloat(monthsBackData[fieldName]) >= 0 ? COLORS[index] : NEGATIVE_COLORS[index];
                                const chartData = {
                                    tenor: fieldName.substring(1),
                                    amount: monthsBackData[fieldName],
                                    color: color
                                };
                                const fieldStats = statisticsByFieldName[fieldName];
                                chartData[STAT_MEDIAN] = fieldStats[STAT_MEDIAN];
                                chartData[STAT_PLUS_1_STD] = fieldStats[STAT_PLUS_1_STD];
                                chartData[STAT_MINUS_1_STD] = fieldStats[STAT_MINUS_1_STD];
                                return chartData;
                            });
                            resolve(dataProvider);
                            return;
                        } else {
                            const currentRequest = this.survivalHorizonService.buildPollingRequestParams();
                            currentRequest.currencies = [this.currency];
                            this.survivalHorizonService.getDataObservable(currentRequest)
                                .pipe(take(1))
                                .subscribe({
                                    next: (serviceData: GetSurvivalHorizonServiceDataOutput) => {
                                        const currentPositions = serviceData.positions;
                                        const bucketingStrategy = this.survivalHorizonService.getBucketingStrategies()
                                            .filter((bs: BucketingStrategy) => {
                                                return (bs instanceof BucketedBucketing);
                                            })[0];
                                        const data: any = this.survivalHorizonService.bucketDataByBlock(
                                            this.currency,
                                            currentPositions,
                                            bucketingStrategy,
                                            {},
                                            (entry: SurvivalHorizonData) => {
                                                return entry.currency;
                                            },
                                            (entry: SurvivalHorizonData) => {
                                            });
                                        const tableData = this.survivalHorizonService.bucketedBlockDataToTableData(data);
                                        resolve(tableData.map((d: any, index: number) => {
                                            let amount = 0;
                                            let tenor = d.bucket;
                                            if (this.metric === MetricTypes.HISTORICAL_BALANCE) {
                                                amount = d[this.currency + '_cumulativeAmount'];
                                            } else if (this.metric === MetricTypes.HISTORICAL_BUFFER) {
                                                amount = d[this.currency + '_bufferAmount'];
                                            } else if (this.metric === MetricTypes.HISTORICAL_SURPLUS) {
                                                amount = d[this.currency + '_liquiditySurplus'];
                                            }
                                            if (index <= 2) {
                                                tenor = index + 'D';
                                            }
                                            const fieldName = 'm' + tenor;
                                            const fieldStats = statisticsByFieldName[fieldName];
                                            const color = amount >= 0 ? COLORS[index] : NEGATIVE_COLORS[index];
                                            const chartData = {
                                                amount: amount,
                                                tenor: tenor,
                                                color: color,
                                            };
                                            chartData[STAT_MEDIAN] = fieldStats[STAT_MEDIAN];
                                            chartData[STAT_PLUS_1_STD] = fieldStats[STAT_PLUS_1_STD];
                                            chartData[STAT_MINUS_1_STD] = fieldStats[STAT_MINUS_1_STD];
                                            return chartData;
                                        }));
                                    }
                                });
                        }
                    });
                }
            });
        /*
        if (this.metric === MetricTypes.THROUGHPUT) {
            // const historicalThroughputData = getDummyThroughputData();
            const throughputHistoricalOutput = new HistoricalThroughputData(historicalThroughputData, 'Throughput');
            historicalData = throughputHistoricalOutput.prepareThroughputTrends(historicalThroughputData, 'amount');
            if (historicalData.length > 0) {
                return new Promise<any[]>((resolve, reject) => {
                    resolve(historicalData);
                    return;
                });
            } else {
                return new Promise<any[]>((resolve, reject) => {
                    resolve([]);
                    return;
                });
            }
        } else {
            return new Promise<any[]>((resolve, reject) => {
            if (!this.currency || !this.metric) {
                resolve([]);
                return;
            }
            const historicalData: HistoricalData[] = this.snapshotService.historicalData(this.currency, this.metric)
                .sort((d1: HistoricalData, d2: HistoricalData) => {
                    return d1.date < d2.date ? 1 : -1;
                });
            
            const historicalBalanceInput = getDummyCumulativeData();
            const historicalCumulativeData = new HistoricalCumulativeData(historicalBalanceInput, 'Cumulative');
            const historicalData = historicalCumulativeData.prepareHistoricalDataOutput(historicalBalanceInput, 'amount')[0]['data'];
            console.log(historicalData);
            const statisticsByFieldName = {};
            HISTORICAL_DATA_METRIC_FIELDS.map((fieldName: string, index: number) => {
                const fieldStats = this.statisticsByFieldName(fieldName, historicalData);
                statisticsByFieldName[fieldName] = fieldStats;
            });
            if (monthsBack > 0) {
                monthsBack = Math.min(monthsBack, historicalData.length);
                const monthsBackData = historicalData
                    .slice(monthsBack - 1, monthsBack)[0];
                if (!monthsBackData) {
                    resolve([]);
                    return;
                }
                const dataProvider = HISTORICAL_DATA_METRIC_FIELDS.map((fieldName: string, index: number) => {
                    const color = parseFloat(monthsBackData[fieldName]) >= 0 ? COLORS[index] : NEGATIVE_COLORS[index];
                    const chartData = {
                        tenor: fieldName.substring(1),
                        amount: monthsBackData[fieldName],
                        color: color
                    };
                    const fieldStats = statisticsByFieldName[fieldName];
                    chartData[STAT_MEDIAN] = fieldStats[STAT_MEDIAN];
                    chartData[STAT_PLUS_1_STD] = fieldStats[STAT_PLUS_1_STD];
                    chartData[STAT_MINUS_1_STD] = fieldStats[STAT_MINUS_1_STD];
                    return chartData;
                });
                resolve(dataProvider);
                return;
            } else {
                const currentRequest = this.survivalHorizonService.buildPollingRequestParams();
                currentRequest.currencies = [this.currency];
                this.survivalHorizonService.getDataObservable(currentRequest)
                    .pipe(take(1))
                    .subscribe({next: (serviceData: GetSurvivalHorizonServiceDataOutput) => {
                        const currentPositions = serviceData.positions;
                        const bucketingStrategy = this.survivalHorizonService.getBucketingStrategies()
                            .filter((bs: BucketingStrategy) => {
                                return (bs instanceof BucketedBucketing);
                            })[0];
                        const data: any = this.survivalHorizonService.bucketDataByBlock(
                            this.currency,
                            currentPositions,
                            bucketingStrategy,
                            {},
                            (entry: SurvivalHorizonData) => {
                                return entry.currency;
                            },
                            (entry: SurvivalHorizonData) => {
                            });
                        const tableData = this.survivalHorizonService.bucketedBlockDataToTableData(data);
                        resolve(tableData.map((d: any, index: number) => {
                            let amount = 0;
                            let tenor = d.bucket;
                            if (this.metric === MetricTypes.HISTORICAL_BALANCE) {
                                amount = d[this.currency + '_cumulativeAmount'];
                            } else if (this.metric === MetricTypes.HISTORICAL_BUFFER) {
                                amount = d[this.currency + '_bufferAmount'];
                            } else if (this.metric === MetricTypes.HISTORICAL_SURPLUS) {
                                amount = d[this.currency + '_liquiditySurplus'];
                            }
                            if (index <= 2) {
                                tenor = index + 'D';
                            }
                            const fieldName = 'm' + tenor;
                            const fieldStats = statisticsByFieldName[fieldName];
                            const color = amount >= 0 ? COLORS[index] : NEGATIVE_COLORS[index];
                            const chartData = {
                                amount: amount,
                                tenor: tenor,
                                color: color,
                            };
                            chartData[STAT_MEDIAN] = fieldStats[STAT_MEDIAN];
                            chartData[STAT_PLUS_1_STD] = fieldStats[STAT_PLUS_1_STD];
                            chartData[STAT_MINUS_1_STD] = fieldStats[STAT_MINUS_1_STD];
                            return chartData;
                        }));
                    });
            }
        });
    }
    */
    }

    private getTenorHistoryData(tenorField: string, color: string): any[] {
        let historicalData = [];
        if (this.metric === MetricTypes.SURVIVAL_HORIZON) {
            const historicalSHInputData = getDummySHData();
            const historicalSHData = new HistoricalSHData(historicalSHInputData, 'Survival Horizon');
            historicalData = historicalSHData.prepareHistoricalDataOutput(historicalSHInputData, 'amount');
        } else {
            const historicalBalanceInput = getDummyCumulativeData();
            const historicalCumulativeData = new HistoricalCumulativeData(historicalBalanceInput, 'Cumulative');
            historicalData = historicalCumulativeData.prepareHistoricalDataOutput(historicalBalanceInput, 'amount')[0]['data'];
        }
        console.log(historicalData);
        return historicalData
            .sort((d1: any, d2: any) => {
                return d1['date'] < d2['date'] ? -1 : 1;
            })
            .map((d: HistoricalData, index: number) => {
                return {
                    date: d.date.substring(0, 7),
                    amount: d[tenorField],
                    color: color
                };
            });
    }

    showTenor(tenor: string, color: string, timeframe: string) {
        const tenorField = 'm' + tenor;
        const tenorHistoryData: any[] = this.getTenorHistoryData(tenorField, color);
        this.createChart(tenorHistoryData, timeframe, true);
    }

    private monthsBackTitle(): string {
        if (this.monthsBack === 0) {
            return 'Current';
        } else {
            return ('-' + this.monthsBack + ' month') + ((this.monthsBack !== 1) ? 's' : '');
        }
    }

    previousChart() {
        this.getHistoricalData(this.monthsBack)
            .then((historicalData: any[]) => {
                this.createChart(historicalData, this.monthsBackTitle());
            });
    }

    createThroughputCharts(dataProvider: HistoricalOutput, chartName: string): AmChart {
        return createSerialChart(this.amCharts, chartName, 'line', false, false,
            dataProvider['data'], dataProvider['xField'], dataProvider['yFields']);
    }

    createChart(dataProvider: any[], timeframe: string, isHistogram?: boolean) {
        const title = this.currency + ' ' + this.metric + ' (' + timeframe + ')';
        const valueTitle = '';
        let columnWidth = 0.8;
        let topRadius = 1;
        let categoryField = 'tenor';
        let stackType = 'none';
        let depth3D = null;
        let angle = null;
        let lineAlpha = 0.1;
        this.chartLevel = 0;
        if (isHistogram) {
            columnWidth = 1.0;
            topRadius = null;
            categoryField = 'date';
            stackType = 'none';
            depth3D = null;
            angle = null;
            lineAlpha = 0.5;
            this.chartLevel = 1;
        }
        this.chart = this.amCharts.makeChart('histochartdiv', {
            'hideCredits': true,
            'theme': 'light',
            'type': 'serial',
            'addClassNames': true,
            'classNamePrefix': 'histo-',
            'titles': [{
                'text': title,
            }],
            'valueAxes': [
                {
                    'id': 'v1',
                    'stackType': stackType,
                    'position': 'left',
                    'title': valueTitle,
                }
            ],
            'columnWidth': columnWidth,
            'startDuration': 0,
            'graphs': [
                {
                    'id': 'c1',
                    'valueAxis': 'v1',
                    'fillAlphas': 0.85,
                    'lineAlpha': lineAlpha,
                    'type': 'column',
                    // 'topRadius': topRadius,
                    'valueField': 'amount',
                    'colorField': 'color',
                    'showHandOnHover': !isHistogram,
                },
                {
                    'id': 'l1',
                    'valueField': STAT_MEDIAN,
                    'valueAxis': 'v1',
                    'lineAlpha': 0.8,
                    'type': 'line',
                    'lineThickness': 3,
                    'bullet': 'diamond',
                    'lineColor': '#6200EA'
                    // 'bulletBorderAlpha': 1,
                    // 'bulletBorderColor': '#fff',
                },
                {
                    'id': 'l3',
                    'valueAxis': 'v1',
                    'valueField': STAT_MINUS_1_STD,
                    'lineAlpha': 0.8,
                    'type': 'line',
                    'lineThickness': 1,
                    'dashLength': 2,
                    'bullet': 'none',
                    'lineColor': '#7E57C3',
                    // 'bulletBorderAlpha': 0.5,
                    // 'bulletBorderColor': 'black',
                },
                {
                    'id': 'l2',
                    'valueAxis': 'v1',
                    'valueField': STAT_PLUS_1_STD,
                    'lineAlpha': 0.8,
                    'type': 'line',
                    'lineThickness': 1,
                    'dashLength': 2,
                    'bullet': 'none',
                    'lineColor': '#7E57C2',
                    'fillAlphas': 0.2,
                    'fillToGraph': 'l3',
                    // 'bulletBorderAlpha': 1,
                    // 'bulletBorderColor': '#fff',
                }
            ],
            'chartCursor': {
                'pan': true,
                'valueBalloonsEnabled': false,
                'avoidBalloonOverlapping': true,
                'valueLineEnabled': true,
                'valueLineBalloonEnabled': true,
                'cursorAlpha': 0,
                'valueLineAlpha': 0.2
            },
            'plotAreaFillAlphas': 0.1,
            'depth3D': depth3D,
            'angle': angle,
            // 'columnSpacing3D': 10,
            'categoryField': categoryField,
            'categoryAxis': {
                'gridPosition': 'start'
            },
            'dataProvider': dataProvider,
            'responsive': {
                'enabled': true
            },
            'listeners': [
                // {
                //     'event': 'clickGraphItem',
                //     'method': (event) => {
                //         if (!isHistogram) {
                //             this.showTenor(event.item.category, COLORS[event.index], 'Historical ' + event.item.category);
                //         }
                //     }
                // },
                // {
                //     'event': 'clickGraph',
                //     'method': (event) => {
                //         if (!isHistogram) {
                //             // const categoryX = event.event.layerX;
                //             // const categoryX = event.event.clientX - event.event.screenX;
                //             const chartData: any[] = event.chart.chartData;
                //             const firstAxisX = chartData[0].x.categoryAxis;
                //             const clickRelativeX = event.event.clientX - firstAxisX;
                //             const candidateTenors = chartData.filter((chartDataElement: any) => {
                //                 return (chartDataElement.x.categoryAxis <= clickRelativeX);
                //             })
                //             .map((chartDataElement: any) => {
                //                 return chartDataElement.category;
                //             });
                //             const tenor = candidateTenors[candidateTenors.length-1]
                //             this.showTenor(tenor, COLORS[candidateTenors.length-1], 'Historical ' + tenor);
                //         }
                //     }
                // }

                {
                    'event': 'init',
                    'method': function (event) {
                        event.chart.chartCursor.addListener('changed', function (innerEvent) {
                            event.chart.cursorDataContext = event.chart.dataProvider[innerEvent.index];
                        });
                    }
                },
                {
                    'event': 'rendered',
                    'method': (event) => {
                        event.chart.chartDiv.addEventListener('click', () => {
                            const tenor = event.chart.cursorDataContext.tenor;
                            this.showTenor(tenor, event.chart.cursorDataContext.color, 'Historical ' + tenor);
                        });
                    }
                }
            ]
        });
    }

    // median, median +1std, median -1std
}


